import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import ConfirmModal from '../components/ConfirmModal';
import { DSBadge, DSButton, DSCard, DSCardFooterButton, DSCheckbox, DSIcon, DSInput, DSPageHeader, DSSheet, DSTextarea } from '../design-system/primitives';
import './EventCategoryManagement.css';

function EventCategoryManagement({ embedded = false }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_default_for_pinball: false
  });
  const [formTouched, setFormTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/event-categories');
      setCategories(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load event categories');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormTouched(true);
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingCategory) {
        await apiService.put(`/event-categories/${editingCategory.id}`, formData);
      } else {
        await apiService.post('/event-categories', formData);
      }
      
      setShowCreateForm(false);
      setEditingCategory(null);
      setFormTouched(false);
      setFormData({
        name: '',
        description: '',
        is_default_for_pinball: false
      });
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormTouched(false);
    setFormData({
      name: category.name,
      description: category.description || '',
      is_default_for_pinball: category.is_default_for_pinball
    });
    setShowCreateForm(true);
  };

  const handleDelete = (categoryId, categoryName) => {
    setConfirmState({
      title: 'Delete Category?',
      message: `Delete category "${categoryName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          await apiService.delete(`/event-categories/${categoryId}`);
          setConfirmState(null);
          // Deleting from inside the edit sheet leaves nothing to edit
          if (editingCategory && editingCategory.id === categoryId) {
            closeForm();
          }
          fetchCategories();
        } catch (err) {
          if (err.response?.status === 409) {
            setError(`Cannot delete "${categoryName}" because it's assigned to events`);
          } else {
            setError('Failed to delete category');
          }
          console.error('Error:', err);
        } finally {
          setConfirmSubmitting(false);
        }
      }
    });
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingCategory(null);
    setFormTouched(false);
    setFormData({
      name: '',
      description: '',
      is_default_for_pinball: false
    });
    setError(null);
  };

  // Matches the series sheet: the overlay and Escape can both dismiss, so unsaved work
  // gets a prompt and an untouched form just closes.
  const handleCancel = () => {
    if (!formTouched) {
      closeForm();
      return;
    }

    setConfirmState({
      title: editingCategory ? 'Discard Changes?' : 'Discard This Category?',
      message: editingCategory
        ? 'Your edits to this category have not been saved. Close without saving?'
        : 'This category has not been created yet. Close without saving?',
      confirmLabel: 'Discard',
      danger: true,
      onConfirm: () => {
        setConfirmState(null);
        closeForm();
      }
    });
  };

  if (loading) return <div className="loading">Loading categories...</div>;

  return (
    <div className={`event-category-management${embedded ? ' embedded' : ''}`}>
      {/* Adding belongs to the list, as a bar at the end of it. A floating button above
          the card competed with the hub's own Create control and sat nowhere in
          particular. */}
      {!embedded && <DSPageHeader title="Event Categories" />}

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <DSSheet
          title={editingCategory ? 'Edit Category' : 'New Category'}
          onClose={handleCancel}
          footer={(
            <>
              {editingCategory && (
                <DSButton
                  type="button"
                  appearance="text"
                  tone="danger"
                  className="category-sheet__delete"
                  onClick={() => handleDelete(editingCategory.id, editingCategory.name)}
                  disabled={submitting}
                >
                  Delete Category
                </DSButton>
              )}
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </DSButton>
              <DSButton
                type="submit"
                form="event-category-form"
                appearance="solid"
                tone="primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
              </DSButton>
            </>
          )}
        >
          <form id="event-category-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <DSInput
                label="Category Name *"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Capital Cup, Monthly Championship"
              />
            </div>

            <div className="form-group">
              <DSTextarea
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                placeholder="Optional description..."
              />
            </div>

            <div className="form-group">
              <DSCheckbox
                id="category-default-for-pinball"
                name="is_default_for_pinball"
                label="Default category for pinball events (auto-selected)"
                checked={formData.is_default_for_pinball}
                onChange={handleInputChange}
              />
            </div>
          </form>
        </DSSheet>
      )}

      <div className="categories-list">
        {categories.length === 0 ? (
          <DSCard cornerVariant="md" className="categories-empty">
            <p>No categories yet.</p>
            <DSButton appearance="outline" tone="secondary" size="sm" onClick={() => setShowCreateForm(true)}>
              + Add category
            </DSButton>
          </DSCard>
        ) : (
          <DSCard
            frame="bars"
            flush
            cornerVariant="md"
            className="categories-card"
            footer={(
              <DSCardFooterButton showArrow={false} onClick={() => setShowCreateForm(true)}>
                + Add category
              </DSCardFooterButton>
            )}
          >
            {categories.map(category => (
              <div key={category.id} className="category-row">
                <div className="category-row__main">
                  <div className="category-row__title">
                    <span className="category-row__name">{category.name}</span>
                    {category.is_default_for_pinball && (
                      <DSBadge variant="success" size="sm" uppercase>Default</DSBadge>
                    )}
                  </div>
                  {category.description && (
                    <p className="category-row__description">{category.description}</p>
                  )}
                  <p className="category-row__usage">
                    {category.event_count > 0
                      ? `Used by ${category.event_count} event${category.event_count === 1 ? '' : 's'}`
                      : 'Not used yet'}
                  </p>
                </div>
                <div className="category-row__actions">
                  <DSButton
                    appearance="text"
                    tone="secondary"
                    size="sm"
                    iconOnly
                    aria-label={`Edit ${category.name}`}
                    leadingIcon={<DSIcon name="edit" size="sm" tone="brand" />}
                    onClick={() => handleEdit(category)}
                  />
                </div>
              </div>
            ))}
          </DSCard>
        )}

        <p className="categories-note">
          The default category is applied to new events unless you pick another one.
        </p>
      </div>

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default EventCategoryManagement;
